import { formatAsDollars } from '../../utils/format.ts';
import { and, or } from 'tio-ui/utilities';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import FeedContainer from './feed/feed-container.gts';
import ItemComment from './feed/item-comment.gts';
import ItemDetails from './feed/item-details.gts';
import Chip from 'tio-ui/components/chip';
import type { AppHistoryBase } from '../../types/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';
import type { ItemSignature } from './feed/feed-container.gts';
import type TasApplicationModel from 'tio-data/models/tas-application';

interface ApplicationHistoryComponentSignature {
  Args: {
    history: AppHistoryBase[];
    type?: 'application' | 'program';
    title?: string;
    application?: TasApplicationModel;
  };
}

class ApplicationHistory extends Component<ApplicationHistoryComponentSignature> {
  @service declare intl: IntlService;

  get formattedHistory(): ItemSignature[] {
    return this.args.history
      .map((item: AppHistoryBase) => {
        let icon;
        let details;
        const formattedName = item.approverName
          ? ' by ' + item.approverName + (item.role ? ` - ${item.role}` : '')
          : '';
        const baseTranslationString =
          this.args.type === 'program'
            ? 'tuition_assistance.program_instances.history'
            : 'tuition_assistance.applications.history';

        switch (item.logType) {
          case 'assistance': {
            icon = 'CurrencyDollar';
            details = this.intl.t(`${baseTranslationString}.${item.assistanceType}`, {
              amount: formatAsDollars(Number(item.amount) || 0),
              name: item.approverName ? ' by ' + item.approverName : '',
            });
            break;
          }
          case 'approval': {
            icon = 'Document';
            const updateType =
              item.from === item.to ? 'approved_total_set' : 'approved_total_updated';
            details = this.intl.t(`${baseTranslationString}.${updateType}`, {
              amount: formatAsDollars(Number(item.to) || 0),
              from: formatAsDollars(Number(item.from) || 0),
              to: formatAsDollars(Number(item.to) || 0),
              name: item.approverName ? ' by ' + item.approverName : '',
            });
            break;
          }
          case 'state_transition': {
            icon = 'UserIcon';
            details = this.intl.t(`${baseTranslationString}.${item.stateTransitionType}`, {
              name: formattedName,
            });

            if (
              item?.stateTransitionType?.includes('NOT_APPROVED') ||
              item?.stateTransitionType?.includes('REJECTED')
            ) {
              icon = 'HandThumbDownIcon';
            } else if (item?.stateTransitionType?.includes('APPROVED')) {
              icon = 'HandThumbUpIcon';
            } else if (
              item.stateTransitionType &&
              ['COMPLETED', 'PRIMED'].includes(item.stateTransitionType)
            ) {
              icon = 'CheckIcon';
            }

            break;
          }
        }

        return {
          ...item,
          details,
          icon,
        } as ItemSignature;
      })
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });
  }

  <template>
    <VStack>
      <Section>
        <:header>
          <div class="tio-row-tight">
            <span>{{@title}}</span>
            {{#if (and @application @application.inRepayment)}}
              <Chip @intent="warning">
                {{t "tuition_assistance.application_details.in_repayment"}}
              </Chip>
            {{/if}}
          </div>
        </:header>
        <:body>
          <FeedContainer @feed={{this.formattedHistory}} class="[&_ul]:overflow-visible">
            <:record as |item|>
              <ItemDetails @details={{item.details}} @date={{item.date}} class="min-h-12 !mb-0" />
            </:record>
            <:comment as |item|>
              <ItemComment
                @approverComment={{item.approverComment}}
                @approverName={{or item.approverName "System"}}
              />
            </:comment>
          </FeedContainer>
        </:body>
      </Section>
    </VStack>
  </template>
}

export default ApplicationHistory;
